import { useLocalization } from '@components/Localization'
import LocalizedLink from '@components/Localization/LocalizedLink'
import { useNotifications } from '@components/NotificationsProvider'
import { Box, Flex, Grid } from '@gassan-ui'
import {
  Gender,
  LeadType,
  SubmitContactFormMutationVariables,
  useSubmitContactFormMutation
} from '@generated'
import { countryCodes } from '@lib/countryCodes'
import { Button, Field, Heading, Link, RolexContainer, Text } from '@rolex-ui'
import { Form, Formik } from 'formik'
import { rem } from 'polished'
import React, { FC } from 'react'
import * as ReactGA from 'react-ga'
import { BackgroundColorProps } from 'styled-system'
import { schema } from './schema'

const personalFields = {
  gender: '',
  firstName: '',
  lastName: '',
  email: '',
  countryCode: '31',
  phone: ''
}

const locationFields = {
  location: '',
  store: ''
}

const messageCheckboxFields = {
  message: '',
  agree: false,
  subscribe: false
}

type Props = BackgroundColorProps & {
  showStores?: boolean
  subject?: string
  store?: string
  productName?: string
  location?: string
}

const ContactForm: FC<Props> = ({
  showStores = false,
  backgroundColor = 'shades.100',
  subject,
  store,
  location,
  productName
}) => {
  const notifications = useNotifications()
  const [{ fetching }, submitContactForm] = useSubmitContactFormMutation()
  const { messages } = useLocalization()
  return (
    <Box py="10vh" bg={backgroundColor}>
      <RolexContainer id="contact">
        <Box maxWidth="650px" width="90vw" m="0 auto">
          <Heading variant="mediumTitle" as="h3">
            {messages.rolex.contact.contactUs}
          </Heading>
          <Text mb={rem(20)}>{messages.rolex.contact.contactFormBody}</Text>
          <Formik
            validationSchema={schema}
            validateOnBlur
            validateOnChange
            initialValues={{
              ...personalFields,
              ...(showStores && locationFields),
              store,
              location,
              ...messageCheckboxFields
            }}
            onSubmit={async (values, { resetForm }) => {
              const {
                gender,
                firstName,
                lastName,
                email,
                phone,
                message,
                subscribe,
                store,
                location
              } = values
              let variables: SubmitContactFormMutationVariables = {
                input: {
                  type: LeadType.Question,
                  gender: gender as Gender,
                  name: firstName + ' ' + lastName,
                  email,
                  phone,
                  subject,
                  message,
                  store,
                  location,
                  productName,
                  referrer: window.location.href,
                  referrerTitle: document.title
                },
                newsletterSignup: subscribe
              }
              const { data, error } = await submitContactForm(variables)
              if (error) {
                if (process.env.NODE_ENV === 'development') {
                  console.error(error)
                }
                notifications.addNotification({
                  variant: 'error',
                  label: messages.notifications.refreshAndTryAgain
                })
              }

              if (data) {
                notifications.addNotification({
                  variant: 'confirmation',
                  description: messages.notifications.requestSent,
                  label: messages.notifications.weWillContactYou
                })
                resetForm()
                if (['staging', 'production'].includes(process.env.NEXT_PUBLIC_STAGE)) {
                  // @ts-ignore
                  _satellite.track('messages.rolex.contactForm')
                  ReactGA.event({
                    category: 'ContactForm',
                    action: 'Send',
                    label: 'Rolex'
                  })
                }
              }
            }}
          >
            {({ resetForm, values, errors }) => (
              <Form>
                <Grid gridTemplateColumns={['1fr 3fr', '1fr 2fr 50%']} gridColumnGap="1.5rem">
                  <Field
                    name="gender"
                    label={messages.rolex.contactForm.salutation}
                    field="select"
                    placeholder=" "
                  >
                    <option value="">{messages.rolex.contactForm.salutation}</option>
                    <option value={Gender.Male}>{messages.rolex.contactForm.mr}</option>
                    <option value={Gender.Female}>{messages.rolex.contactForm.mrs}</option>
                  </Field>
                  <Field
                    name="firstName"
                    label={messages.rolex.contactForm.firstName}
                    field="input"
                    placeholder=" "
                  />
                  <Field
                    name="lastName"
                    gridColumn={['1 / span 2', 'auto']}
                    label={messages.rolex.contactForm.lastName}
                    field="input"
                    placeholder=" "
                  />
                </Grid>
                <Grid gridTemplateColumns={['1fr 3fr', '3fr 1fr 2fr']} gridColumnGap="1.5rem">
                  <Field
                    name="email"
                    gridColumn={['1 / span 2', 'auto']}
                    label={messages.rolex.contactForm.email}
                    field="input"
                    placeholder=" "
                  />
                  <Field
                    name="countryCode"
                    label={messages.rolex.contactForm.countryCode}
                    field="select"
                  >
                    {countryCodes.map((code, index) => (
                      <option value={code.value} key={index} data-countrycode={code.countrycode}>
                        {code.text}
                      </option>
                    ))}
                  </Field>
                  <Field
                    name="phone"
                    label={messages.rolex.contactForm.phone}
                    field="input"
                    placeholder=" "
                  />
                </Grid>
                {showStores && (
                  <Grid gridTemplateColumns={['1fr', '1fr 1fr']} gridColumnGap="1.5rem">
                    <Field
                      name="location"
                      label={messages.rolex.contactForm.location}
                      field="select"
                      placeholder=" "
                    >
                      <option value="">{messages.rolex.contactForm.location}</option>
                      <option value="amsterdam">Amsterdam</option>
                      <option value="singapore">Singapore</option>
                    </Field>
                    <Field
                      disabled={values.location === ''}
                      name="store"
                      label={messages.rolex.contactForm.store}
                      field="select"
                      placeholder=" "
                    >
                      {values.location === 'singapore' ? (
                        <>
                          <option>{messages.rolex.contactForm.store}</option>
                          <option>GASSAN Singapore Changi Airport | Terminal 1</option>
                          <option>GASSAN Singapore Changi Airport | Terminal 4</option>
                          <option>
                            GASSAN Rolex Boutique Singapore Changi Airport | Terminal 3
                          </option>
                        </>
                      ) : (
                        <>
                          <option>{messages.rolex.contactForm.store}</option>
                          <option>GASSAN Headquarters</option>
                          <option>GASSAN Dam Square</option>
                          <option>Rolex Boutique Schiphol</option>
                        </>
                      )}
                    </Field>
                  </Grid>
                )}
                <Box>
                  <Field
                    name="message"
                    field="textarea"
                    label={messages.rolex.contactForm.message}
                    placeholder={messages.rolex.contactForm.message}
                  />
                  <Field
                    name="agree"
                    label={
                      <Text as="span" variant="description">
                        {messages.rolex.contactForm.privacyNotice}
                        <LocalizedLink as={messages.navigation.privacyPolicy} href="/[...slug]">
                          <a
                            style={{ fontWeight: 'bold' }}
                            href={messages.navigation.privacyPolicy}
                          >
                            {messages.rolex.contactForm.privacyPolicy}
                          </a>
                        </LocalizedLink>
                      </Text>
                    }
                    field="checkbox"
                  />
                  <Field
                    name="subscribe"
                    label={messages.rolex.contactForm.subscribe}
                    field="checkbox"
                  />
                </Box>
                <Flex flexDirection="row-reverse" mt="30px">
                  <Button type="submit">
                    {fetching
                      ? messages.rolex.contactForm.sending
                      : messages.rolex.contactForm.sendMessage}
                  </Button>
                  <Link variant="tertiary" px="4" onClick={() => resetForm()}>
                    {messages.rolex.contactForm.cancel}
                  </Link>
                </Flex>
                <Text textAlign="right" pt={['30px', '40px']}>
                  {messages.rolex.contactForm.required}
                </Text>
              </Form>
            )}
          </Formik>
        </Box>
      </RolexContainer>
    </Box>
  )
}

export default ContactForm
